import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter, Redirect } from "react-router-dom";
import BreadcrumbAdapter from "components/adapters/BreadcrumbAdapter";
import TemplateAdapter from "components/adapters/TemplateAdapter";
import configAdapter, { getBasicAuthHeaders } from "utils/configAdapter";
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    message,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tooltip,
    Typography,
} from "antd";
import {
    CalendarOutlined,
    DollarOutlined,
    FileExcelOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ptBR from "antd/es/date-picker/locale/pt_BR";
import { useMutation, useQuery, useQueryClient } from "react-query";
import * as StatusManagerActions from "../../../../actions/StatusManagerActions";
import * as RedirectActions from "../../../../actions/RedirectActions";
import Core from "../../../../class/Core";

dayjs.extend(customParseFormat);
dayjs.locale("pt-br");

const config = configAdapter;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// util: extrai filename de Content-Disposition (suporta filename* RFC5987)
function getFilenameFromContentDisposition(header) {
    if (!header) return null;

    // tenta filename* (RFC5987) primeiro: filename*=UTF-8''nome%20com%20espaço.xlsx
    const filenameStarMatch = header.match(/filename\*\s*=\s*([^;]+)/i);
    if (filenameStarMatch) {
        let value = filenameStarMatch[1].trim();
        // remove possíveis quotes
        if (value.startsWith("'") || value.startsWith('"'))
            value = value.slice(1, -1);
        // valor esperado: UTF-8''%E2%82%AC%20nome.xlsx
        const parts = value.split("''");
        if (parts.length === 2) {
            try {
                return decodeURIComponent(parts[1]);
            } catch (e) {
                return parts[1];
            }
        }
        return value;
    }

    // tenta filename="nome.xlsx"
    const filenameMatch = header.match(/filename\s*=\s*("(.*?)"|([^;]+))/i);
    if (filenameMatch) {
        const raw = filenameMatch[2] || filenameMatch[3] || filenameMatch[1];
        // remove possíveis aspas
        return raw.replace(/^"(.*)"$/, "$1").trim();
    }

    return null;
}

const ListagemSeguradora = (props) => {
    useEffect(() => {
        props.action.showManager();
        props.action.redirectClear();
    }, [props.action]);

    const { Option } = Select;
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    // State management / Gerenciamento de estado
    const [tableData, setTableData] = useState([]);
    const [showTable, setShowTable] = useState(false);
    const [statistics, setStatistics] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [formValid, setFormValid] = useState(false);
    const [planosPermitidos, setPlanosPermitidos] = useState([]);
    const [hasAccessPermission, setHasAccessPermission] = useState(true);
    const [permissionMessage, setPermissionMessage] = useState("");

    const favorecidos = {
        tokioMarine: 2407,
        chubbSegurosBrasil: 2546,
        vidaEmGrupoPlanoBronze: 2477,
        vidaEmGrupoPlanoPrata: 2335,
        vidaEmGrupoPlanoOuro: 2479,
    };

    // vai para select options
    const GrupoPlanosArray = [
        {
            value: 0,
            label: "PLANO OURO",
            planos: null,
            //planos: [779],
            favorecido: favorecidos.vidaEmGrupoPlanoOuro,
        },
        {
            value: 1,
            label: "PLANO PRATA",
            planos: null,
            //planos: [377, 721, 749, 772, 773, 827, 835],
            favorecido: favorecidos.vidaEmGrupoPlanoPrata,
        },
        {
            value: 2,
            label: "PLANO BRONZE",
            planos: null,
            //planos: [657, 778, 788, 792, 803, 811, 812, 833, 851],
            favorecido: favorecidos.vidaEmGrupoPlanoBronze,
        },
        {
            value: 3,
            label: "VIDA EM GRUPO - POSTOS GO",
            planos: [223],
            favorecido: favorecidos.chubbSegurosBrasil,
        },
        {
            value: 4,
            label: "TOKIO MARINE - PLANO 598",
            planos: [598],
            favorecido: favorecidos.tokioMarine,
        },
        {
            value: 5,
            label: "TOKIO MARINE - PLANO 658",
            planos: [658],
            favorecido: favorecidos.tokioMarine,
        },
        {
            value: 6,
            label: "TOKIO MARINE - PLANO 764",
            planos: [764],
            favorecido: null,
            //favorecido: favorecidos.tokioMarine,
        },
    ];

    // React Query for loading plans / React Query para carregar planos
    const { data: planos = [], isLoading: loadingPlanos } = useQuery(
        ["planos-seguradoras"],
        async () => {
            const response = await fetch(
                config[process.env.NODE_ENV].url +
                "/planos/sem-seguro/cobranca",
                {
                    method: "GET",
                    headers: new Headers(getBasicAuthHeaders()),
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            // Filter and format plans / Filtrar e formatar planos
            const planosFormatted = result.content
                .filter((plano) => plano.id_tr_plno !== 0)
                .filter((plano) => plano.id_tr_plno !== null)
                .filter((plano) => plano.id_tr_plno !== undefined)
                .filter((plano) => plano.id_tr_plno !== 9999)
                .sort((a, b) => a.id_tr_plno - b.id_tr_plno)
                .map((plano) => ({
                    id: plano.id_tr_plno,
                    codigo: plano.id_tr_plno,
                    descricao: plano.dscr_plno,
                }));

            return planosFormatted;
        },
        {
            staleTime: 5 * 60 * 1000, // 5 minutes / 5 minutos
            cacheTime: 10 * 60 * 1000, // 10 minutes / 10 minutos
            retry: 2,
            refetchOnWindowFocus: false,
            onError: (error) => {
                console.error("Error loading plans:", error);
                message.error("Erro ao carregar planos");
            },
        },
    );

    // React Query mutation for report data / React Query mutation para dados do relatório
    const { mutate: executeReport, isLoading: loadingReport } = useMutation(
        async ({ params, isExcel }) => {
            const response = await fetch(
                config[process.env.NODE_ENV].url +
                "/relatorio/listagem-seguradora",
                {
                    method: "POST",
                    headers: new Headers(getBasicAuthHeaders()),
                    body: JSON.stringify(params),
                },
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (isExcel) {
                // Handle Excel download / Manipular download Excel
                const blob = await response.blob();

                if (blob.size === 0) {
                    throw new Error("Arquivo Excel vazio recebido");
                }

                const contentDisposition =
                    response.headers.get("Content-Disposition") ||
                    response.headers.get("content-disposition");

                let filename =
                    getFilenameFromContentDisposition(contentDisposition);

                // fallback se não vier filename
                if (!filename) {
                    const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
                    filename = `listagem_acordos_definitivo_${timestamp}.xlsx`;
                }

                // Create download / Criar download
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.style.display = "none";
                a.href = url;
                a.download = filename;

                document.body.appendChild(a);
                a.click();

                // Cleanup / Limpeza
                setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }, 100);

                return {
                    success: true,
                    message: `Arquivo Excel "${filename}" baixado com sucesso!`,
                    type: "excel",
                };
            } else {
                // Handle JSON response / Manipular resposta JSON
                const result = await response.json();
                return {
                    success: true,
                    data: result,
                    type: "json",
                };
            }
        },
        {
            retry: 1,
            onError: (error) => {
                console.error("Error in report request:", error);

                // Show specific error message / Mostrar mensagem de erro específica
                if (error.message.includes("HTTP error! status: 400")) {
                    message.error(
                        "Parâmetros inválidos. Verifique os filtros e tente novamente.",
                    );
                } else if (error.message.includes("HTTP error! status: 401")) {
                    message.error("Sessão expirada. Faça login novamente.");
                } else if (error.message.includes("HTTP error! status: 403")) {
                    message.error(
                        "Acesso negado. Você não tem permissão para acessar este recurso.",
                    );
                } else if (error.message.includes("HTTP error! status: 500")) {
                    message.error(
                        "Erro interno do servidor. Tente novamente mais tarde.",
                    );
                } else if (error.message.includes("HTTP error")) {
                    message.error(`Erro do servidor: ${error.message}`);
                } else if (error.message.includes("Excel")) {
                    message.error(`Erro no download Excel: ${error.message}`);
                } else if (error.message.includes("Failed to fetch")) {
                    message.error(
                        "Erro de conexão. Verifique sua internet e tente novamente.",
                    );
                } else {
                    message.error(
                        `Erro ao processar solicitação: ${error.message}`,
                    );
                }
            },
        },
    );

    const {
        mutate: executeExcelPreliminary,
        isLoading: loadingExcelPreliminary,
    } = useMutation(async ({ params }) => {
        const response = await fetch(
            config[process.env.NODE_ENV].url +
            "/relatorio/listagem-seguradora-preliminar",
            {
                method: "POST",
                headers: new Headers(getBasicAuthHeaders()),
                body: JSON.stringify(params),
            },
        );

        const blob = await response.blob();

        if (blob.size === 0) {
            throw new Error("Arquivo Excel vazio recebido");
        }

        // tenta extrair filename do header
        const contentDisposition =
            response.headers.get("Content-Disposition") ||
            response.headers.get("content-disposition");

        let filename = getFilenameFromContentDisposition(contentDisposition);

        // fallback se não vier filename
        if (!filename) {
            const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
            filename = `listagem_acordos_preliminar_${timestamp}.xlsx`;
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);

        return {
            success: true,
            message: `Arquivo Excel "${filename}" baixado com sucesso!`,
            type: "excel",
        };
    });

    // Default form values / Valores padrão do formulário
    const defaultForm = {
        planos: null,
        dateRange: null,
    };

    // Initialize form / Inicializar formulário
    useEffect(() => {
        form.setFieldsValue(defaultForm);
    }, []);

    // Verificar permissões do usuário ao carregar o componente
    useEffect(() => {
        // Verificar se o usuário está autenticado e tem configurações
        if (!props.user || !props.user.config) {
            setHasAccessPermission(false);
            setPermissionMessage(
                "Usuário não autenticado ou sem configurações de acesso.",
            );
            return;
        }

        // Acessar grupo_seguro do usuário
        const grupoSeguro =
            props.user?.config?.grupo?.pmtr?.relatorios?.grupo_seguro;

        // Caso 1: grupo_seguro não existe ou não é array
        if (!grupoSeguro || !Array.isArray(grupoSeguro)) {
            setHasAccessPermission(false);
            setPermissionMessage(
                "Você não possui permissões configuradas para acessar relatórios de acordos.",
            );
            setPlanosPermitidos([]);
            return;
        }

        // Caso 2: grupo_seguro é array vazio
        if (grupoSeguro.length === 0) {
            setHasAccessPermission(false);
            setPermissionMessage(
                "Você não possui permissões para acessar nenhum plano de acordos.",
            );
            setPlanosPermitidos([]);
            return;
        }

        // Caso 3: grupo_seguro tem valores - filtrar planos permitidos
        const planosFiltrados = GrupoPlanosArray.filter((plano) =>
            grupoSeguro.includes(plano.value),
        );

        if (planosFiltrados.length === 0) {
            setHasAccessPermission(false);
            setPermissionMessage(
                "Os planos configurados para você não estão disponíveis no sistema.",
            );
            setPlanosPermitidos([]);
            return;
        }

        // Usuário tem acesso - definir planos permitidos
        setHasAccessPermission(true);
        setPlanosPermitidos(planosFiltrados);
        setPermissionMessage("");
    }, [props.user]);

    // Utility function to transform API data / Função utilitária para transformar dados da API
    const transformApiData = (data) => {
        return data.map((item, index) => ({
            key: `${item.cpf_tbldr}-${index}`,
            nome: item.nome_tbldr,
            cpf: item.cpf_tbldr,
            data_nascimento: item.dt_nasc_tbldr
                ? new Date(item.dt_nasc_tbldr).toLocaleDateString("pt-BR")
                : "",
            data_admissao: item.dt_admi_tbldr
                ? new Date(item.dt_admi_tbldr).toLocaleDateString("pt-BR")
                : "",
            razao_social: item.razao_empr,
            cnpj: item.cnpj_empr,
            situacao: item.grpo_empr_blto_tbldr,
            plano: item.id_tr_plno,
            patronal: item.patronal,
            laboral: item.laboral,
            data_vencimento: item.dt_vcto_empr_blto
                ? new Date(item.dt_vcto_empr_blto).toLocaleDateString("pt-BR")
                : "",
            data_pagamento: item.dt_pgto_empr_blto
                ? new Date(item.dt_pgto_empr_blto).toLocaleDateString("pt-BR")
                : "",
            valor_pago: item.vlr_pago_empr_blto,
            numero: item.nosso_numero,
        }));
    };

    // Utility function to build API parameters / Função utilitária para construir parâmetros da API
    const buildApiParams = (values, isExcel) => {
        if (values.planos === null || values.planos === undefined) {
            throw new Error("Planos é obrigatórios.");
        }

        const params = {
            date_ini:
                values.dateRange && values.dateRange[0]
                    ? values.dateRange[0].format("DD/MM/YYYY")
                    : null,
            date_fim:
                values.dateRange && values.dateRange[1]
                    ? values.dateRange[1].format("DD/MM/YYYY")
                    : null,
            grupo: false,
            pesquisa_parcial: "sim",
            segvida: "sindical",
            grupo_trabalhador_pesquisa: 9,
            vidaemgrupo: "sim",
            excel: isExcel,
        };

        const selectedGrupo = GrupoPlanosArray.find(
            (grupo) => grupo.value === values.planos,
        );
        if (selectedGrupo) {
            params.plano = selectedGrupo.planos;
            params.favorecido = selectedGrupo.favorecido;
            params.nomeDoGrupo = selectedGrupo.label;
        }

        return params;
    };

    // Utility function to build statistics / Função utilitária para construir estatísticas
    const buildStatistics = (data, apiResponse) => {
        return {
            total: data.length,
            ativos: apiResponse?.data?.qtd_ativos || 0,
            afastados_ate_12m: apiResponse?.data?.qtd_afastados_ate_12m || 0,
            afastados_apos_12m: apiResponse?.data?.qtd_afastados_apos_12m || 0,
            acima_limite_idade: apiResponse?.data?.qtd_acima_limite_idade || 0,
            opositores: apiResponse?.data?.qtd_opositores || 0,
            acordos: apiResponse?.data?.qtd_acordos || 0,
        };
    };

    // Handle form submission for table display / Manipular envio do formulário para exibir tabela
    const onFinishTable = async (values) => {
        await handleSubmit(values, false);
    };

    // Handle form submission for Excel download / Manipular envio do formulário para download Excel
    const onFinishExcel = async (values) => {
        await handleSubmit(values, true);
    };

    const onFinishExcelPreliminary = async (values) => {
        await handleSubmitPreliminary(values);
    };

    // Handler para o novo botão "GERAR RELATÓRIO"
    const onFinishGerarRelatorio = async (values) => {
        await handleSubmitGerarRelatorio(values);
    };

    const handleSubmitGerarRelatorio = async (values) => {
        try {
            const params = buildApiParams(values, true);
            // params.acordos = true; // Adiciona parâmetro acordos

            executeReport(
                { params, isExcel: true },
                {
                    onSuccess: (result) => {
                        if (result.type === "excel") {
                            message.success(result.message);
                        }
                    },
                },
            );
        } catch (error) {
            console.error("Erro ao gerar relatório:", error);
            const errorMessage =
                error?.message ||
                "Ocorreu um erro inesperado ao gerar o relatório.";
            message.error(errorMessage);
        }
    };

    // Common submit handler / Manipulador comum de envio
    const handleSubmit = async (values, isExcel) => {
        try {
            // Build API params / Construir parâmetros da API
            const params = buildApiParams(values, isExcel);

            // Execute report request / Executar solicitação do relatório
            executeReport(
                { params, isExcel },
                {
                    onSuccess: (result) => {
                        if (result.type === "excel") {
                            message.success(result.message);
                        } else {
                            // Handle JSON response for table / Manipular resposta JSON para tabela
                            const data = result.data?.data?.content || [];

                            if (data.length === 0) {
                                message.warning(
                                    "Nenhum registro encontrado com os filtros aplicados",
                                );
                                setTableData([]);
                                setShowTable(false);
                                setStatistics(null);
                                return;
                            }

                            // Transform API data / Transformar dados da API
                            const transformedData = transformApiData(data);
                            setTableData(transformedData);
                            setShowTable(true);

                            // Build statistics / Construir estatísticas
                            const stats = buildStatistics(data, result.data);
                            setStatistics(stats);

                            message.success(
                                `${data.length} registros encontrados`,
                            );
                        }
                    },
                },
            );
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            message.error("Erro ao processar solicitação");
        }
    };

    const handleSubmitPreliminary = async (values) => {
        try {
            const params = buildApiParams(values, true);
            executeExcelPreliminary(
                { params },
                {
                    onSuccess: (result) => {
                        message.success(result.message);
                    },
                },
            );
        } catch (error) {
            console.error("Erro ao gerar o relatório preliminar:", error);
            const errorMessage =
                error?.message ||
                "Ocorreu um erro inesperado ao gerar o relatório.";
            message.error(errorMessage);
        }
    };

    // Loading state / Estado de carregamento
    const loading = loadingPlanos || loadingReport || loadingExcelPreliminary;

    // Table columns based on backend data structure / Colunas da tabela baseadas na estrutura de dados do backend
    const columns = [
        {
            title: "Nome",
            dataIndex: "nome",
            key: "nome",
            width: 250,
            ellipsis: true,
            fixed: "left",
        },
        {
            title: "CPF",
            dataIndex: "cpf",
            key: "cpf",
            width: 120,
        },
        {
            title: "Data Nascimento",
            dataIndex: "data_nascimento",
            key: "data_nascimento",
            width: 120,
        },
        {
            title: "Data Admissão",
            dataIndex: "data_admissao",
            key: "data_admissao",
            width: 120,
        },
        {
            title: "Empresa",
            dataIndex: "razao_social",
            key: "razao_social",
            width: 250,
            ellipsis: true,
        },
        {
            title: "CNPJ",
            dataIndex: "cnpj",
            key: "cnpj",
            width: 140,
        },
        {
            title: "Plano",
            dataIndex: "plano",
            key: "plano",
            width: 50,
            align: "center",
        },
        {
            title: "Patronal",
            dataIndex: "patronal",
            key: "patronal",
            width: 100,
            align: "center",
        },
        {
            title: "Laboral",
            dataIndex: "laboral",
            key: "laboral",
            width: 100,
            align: "center",
        },
        {
            title: "Dt Veto",
            dataIndex: "data_vencimento",
            key: "data_vencimento",
            width: 100,
        },
        {
            title: "Dt Pgto",
            dataIndex: "data_pagamento",
            key: "data_pagamento",
            width: 100,
        },
        {
            title: "N.Numero",
            dataIndex: "numero",
            key: "numero",
            width: 100,
            align: "center",
        },
        {
            title: "Situação",
            dataIndex: "situacao",
            key: "situacao",
            width: 100,
            align: "center",
            render: (text) => {
                const situacaoMap = {
                    ativo: { label: "Ativo", color: "#52c41a" },
                    AfastadoAte12Meses: {
                        label: "Afastado ≤12m",
                        color: "#faad14",
                    },
                    AfastadoApos12Meses: {
                        label: "Afastado >12m",
                        color: "#ff7a45",
                    },
                    idadeMaxima: { label: "Idade Máx", color: "#ff4d4f" },
                    opositor: { label: "Opositor", color: "#722ed1" },
                };

                const situacao = situacaoMap[text] || {
                    label: text,
                    color: "#666",
                };

                return (
                    <span
                        style={{
                            color: situacao.color,
                            fontWeight: "bold",
                            fontSize: "11px",
                        }}
                    >
                        {situacao.label}
                    </span>
                );
            },
        },
    ];

    if (props.redirect.url) {
        return <Redirect to={props.redirect.url} />;
    }

    return (
        <TemplateAdapter>
            <Row>
                <Col span={24}>
                    <BreadcrumbAdapter
                        data={[
                            {
                                titulo: "Relatório Prestadores",
                                link: "/",
                            },
                            {
                                titulo: "Lista Acordos",
                            },
                        ]}
                    />
                </Col>
            </Row>

            <div style={{ padding: "0 0 24px 0" }}>
                {/* Header Section / Seção do cabeçalho */}
                <Card
                    style={{ marginBottom: 24 }}
                    bodyStyle={{ padding: "20px 24px" }}
                >
                    <Row align="middle" justify="space-between">
                        <Col>
                            <Title
                                level={2}
                                style={{ margin: 0, color: "#1890ff" }}
                            >
                                <UserOutlined style={{ marginRight: 8 }} />
                                Lista de Acordos
                            </Title>
                            <Text type="secondary" style={{ fontSize: "14px" }}>
                                Configure os filtros desejados (planos, datas) e
                                visualize os dados dos beneficiários com
                                informações como situação, valores pagos e
                                números de controle.
                            </Text>
                        </Col>
                    </Row>
                </Card>

                {/* Filters Section / Seção de filtros */}
                <Card
                    title={
                        <Space>
                            <SearchOutlined />
                            <span>Filtros de Pesquisa</span>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                    bodyStyle={{ padding: "20px 24px" }}
                >
                    <Form
                        form={form}
                        name="filtro-seguradoras"
                        className="form-ant-4"
                        layout="vertical"
                        onValuesChange={(changedValues, allValues) => {
                            const selected = allValues.planos;
                            setSelectedPlan(selected);

                            const { planos, dateRange } = allValues;

                            if (
                                planos >= 0 &&
                                dateRange &&
                                dateRange.length === 2
                            ) {
                                setFormValid(true);
                            } else {
                                setFormValid(false);
                            }
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            {/* Plans filter / Filtro de planos */}
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <Form.Item
                                    name="planos"
                                    label={
                                        <Space>
                                            <span>Planos</span>
                                            <Tooltip title="Selecione os planos específicos para filtrar os resultados">
                                                <InfoCircleOutlined
                                                    style={{ color: "#1890ff" }}
                                                />
                                            </Tooltip>
                                        </Space>
                                    }
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Por favor, selecione pelo menos um plano",
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Selecione os planos"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option?.children
                                                ?.toString()
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                        notFoundContent="Nenhum plano encontrado"
                                        size="large"
                                        options={planosPermitidos}
                                        disabled={
                                            !hasAccessPermission || loading
                                        }
                                    ></Select>
                                </Form.Item>
                            </Col>

                            {/* Date Range filter / Filtro de período */}
                            <Col xs={24} sm={12} md={8} lg={8}>
                                <Form.Item
                                    name="dateRange"
                                    label={
                                        <Space>
                                            <CalendarOutlined />
                                            <span>Período de Pagamento</span>
                                            <Tooltip title="Selecione o período inicial e final para filtrar por data de pagamento">
                                                <InfoCircleOutlined
                                                    style={{ color: "#1890ff" }}
                                                />
                                            </Tooltip>
                                        </Space>
                                    }
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Por favor, selecione o período de datas",
                                        },
                                    ]}
                                >
                                    <RangePicker
                                        locale={ptBR}
                                        style={{ width: "100%" }}
                                        format="DD/MM/YYYY"
                                        placeholder={[
                                            "Data inicial",
                                            "Data final",
                                        ]}
                                        size="large"
                                        allowClear
                                        separator="até"
                                        disabled={
                                            !hasAccessPermission || loading
                                        }
                                    />
                                </Form.Item>
                            </Col>
                            {/* BOTÕES ANTIGOS COMENTADOS - PODEM RETORNAR
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <Row justify="center">
                                    <Col
                                        span={24}
                                        style={{ textAlign: "center" }}
                                    >
                                        <Space size="middle">
                                            <Button
                                                type="primary"
                                                onClick={() =>
                                                    form
                                                        .validateFields()
                                                        .then(onFinishTable)
                                                }
                                                loading={loading}
                                                size="large"
                                                disabled={!formValid}
                                                icon={<SearchOutlined />}
                                                style={{
                                                    minWidth: 216,
                                                }}
                                            >
                                                PESQUISAR
                                            </Button>
                                        </Space>
                                    </Col>
                                    <Col
                                        span={24}
                                        style={{ textAlign: "center" }}
                                    >
                                        <Space size="middle">
                                            <Button
                                                onClick={() =>
                                                    form
                                                        .validateFields()
                                                        .then(onFinishExcel)
                                                }
                                                loading={loading}
                                                size="large"
                                                disabled={!formValid}
                                                icon={<FileExcelOutlined />}
                                                style={{
                                                    minWidth: 216,
                                                }}
                                            >
                                                DEFINITIVO EXCEL
                                            </Button>
                                        </Space>
                                    </Col>
                                    <Col
                                        span={24}
                                        style={{ textAlign: "center" }}
                                    >
                                        <Space size="middle">
                                            <Button
                                                onClick={() =>
                                                    form
                                                        .validateFields()
                                                        .then(
                                                            onFinishExcelPreliminary
                                                        )
                                                }
                                                loading={loading}
                                                size="large"
                                                disabled={!formValid}
                                                icon={<FileExcelOutlined />}
                                                style={{
                                                    minWidth: 216,
                                                }}
                                            >
                                                PRELIMINAR EXCEL
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Col>
                            */}

                            {/* NOVO BOTÃO GERAR RELATÓRIO */}
                            <Col xs={24} sm={24} md={8} lg={8}>
                                <Form.Item label=" " colon={false}>
                                    <Button
                                        type="primary"
                                        onClick={() =>
                                            form
                                                .validateFields()
                                                .then(onFinishGerarRelatorio)
                                        }
                                        loading={loading}
                                        size="large"
                                        disabled={
                                            !formValid ||
                                            !hasAccessPermission ||
                                            loading
                                        }
                                        icon={<FileExcelOutlined />}
                                        style={{ width: "100%" }}
                                    >
                                        GERAR RELATÓRIO
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>

                {/* Alert de Permissão Negada */}
                {!hasAccessPermission && permissionMessage && (
                    <Alert
                        message="Acesso Restrito"
                        description={permissionMessage}
                        type="error"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                )}

                {/* SEÇÃO DE ESTATÍSTICAS COMENTADA - PODE RETORNAR
                {statistics && (
                    <Card
                        title={
                            <Space>
                                <DollarOutlined />
                                <span>Estatísticas do Relatório</span>
                            </Space>
                        }
                        style={{ marginBottom: 24 }}
                        bodyStyle={{ padding: "20px 24px" }}
                    >
                        <Row
                            gutter={[16, 16]}
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Total de Registros"
                                        value={statistics.total}
                                        prefix={
                                            <UserOutlined
                                                style={{ color: "#1890ff" }}
                                            />
                                        }
                                        valueStyle={{ color: "#1890ff" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Ativos"
                                        value={statistics.ativos}
                                        valueStyle={{ color: "#52c41a" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Afastados ≤12m"
                                        value={statistics.afastados_ate_12m}
                                        valueStyle={{ color: "#faad14" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Afastados >12m"
                                        value={statistics.afastados_apos_12m}
                                        valueStyle={{ color: "#ff7a45" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Idade Máxima"
                                        value={statistics.acima_limite_idade}
                                        valueStyle={{ color: "#ff4d4f" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Opositores"
                                        value={statistics.opositores}
                                        valueStyle={{ color: "#722ed1" }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={3}>
                                <Card
                                    size="small"
                                    style={{ textAlign: "center" }}
                                >
                                    <Statistic
                                        title="Acordos"
                                        value={statistics.acordos}
                                        valueStyle={{ color: "#c12ed1" }}
                                    />
                                </Card>
                            </Col>
                        </Row>
                    </Card>
                )}
                */}

                {/* TABELA DE RESULTADOS COMENTADA - PODE RETORNAR
                {showTable && (
                    <Card
                        title={
                            <Space>
                                <span>Resultados da Pesquisa</span>
                            </Space>
                        }
                        bodyStyle={{ padding: "20px 24px" }}
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Alert
                                message={`${tableData.length} registros encontrados`}
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        </div>

                        <Table
                            columns={columns}
                            dataSource={tableData}
                            rowKey="key"
                            loading={loading}
                            pagination={{
                                pageSize: 50,
                                showSizeChanger: true,
                                showQuickJumper: false,
                                showTotal: (total, range) =>
                                    `${range[0]}-${range[1]} de ${total} registros`,
                                pageSizeOptions: ["20", "50", "100", "200"],
                                position: ["bottomCenter"],
                            }}
                            scroll={{ x: 1200 }}
                            size="small"
                            bordered
                            style={{
                                backgroundColor: "#fafafa",
                                borderRadius: "6px",
                            }}
                        />
                    </Card>
                )}
                */}

                {/* Empty state / Estado vazio */}
                {!showTable && !loading && (
                    <Card style={{ textAlign: "center", padding: "40px 20px" }}>
                        <div style={{ color: "#999" }}>
                            <SearchOutlined
                                style={{
                                    fontSize: "48px",
                                    marginBottom: "16px",
                                }}
                            />
                            <Title level={4} type="secondary">
                                {!hasAccessPermission
                                    ? "Você não possui permissão para acessar esta funcionalidade"
                                    : 'Configure os filtros e clique em "GERAR RELATÓRIO" para baixar o arquivo Excel'}
                            </Title>
                            <Text type="secondary">
                                {!hasAccessPermission
                                    ? "Entre em contato com o administrador do sistema para solicitar acesso"
                                    : "Use os filtros acima para selecionar o período e plano desejados"}
                            </Text>
                        </div>
                    </Card>
                )}
            </div>
        </TemplateAdapter>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.user,
        managerStatus: state.managerStatus,
        redirect: state.redirectReducer,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        action: bindActionCreators(
            {
                ...StatusManagerActions,
                ...RedirectActions,
            },
            dispatch,
        ),
    };
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ListagemSeguradora),
);
